import { z } from 'zod';
import { ORDERED_STAGES } from './opportunity.js';
// ── Board customization engine (config) ──────────────────────────────────────
// Per-business workflow config stored on businesses.stage_config. v1 = stage visibility;
// custom stages + per-stage sections/on-enter actions extend this object in later slices.
/** Stages an agency may hide/show as board columns. 'audition' is the entry lane and always stays. */
export const TOGGLEABLE_STAGES = ORDERED_STAGES.filter((s) => s !== 'audition');
/**
 * A custom kanban stage an agency adds beyond the built-in pipeline. `key` is `c_`-prefixed so it can
 * NEVER collide with a built-in enum value; `after` is the built-in stage it sits after on the board.
 */
export const customStageSchema = z.object({
    key: z.string().regex(/^c_[a-z0-9_]{1,30}$/, 'invalid custom stage key'),
    label: z.string().trim().min(1).max(40),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    after: z.string().max(40).optional(),
});
/** Build a `c_` key from a label (slug), e.g. "Network test" → "c_network_test". */
export const customStageKey = (label) => {
    const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 30);
    return slug ? `c_${slug}` : 'c_stage';
};
/** Optional sections that can be shown on an audition, gated by its stage (engine: per-stage sections). */
export const AUDITION_SECTIONS = ['contacts', 'feedback'];
/** Shown on every stage unless an agency overrides per stage — preserves current behavior by default. */
export const DEFAULT_AUDITION_SECTIONS = ['contacts', 'feedback'];
/**
 * A one-tap shortcut button on a card (engine: customizable quick actions). Exactly one effect:
 * `stage` moves the card to a built-in stage / `'passed'` / a `c_` custom lane; OR `submitted` flips
 * the submitted flag. Shown on submitted cards on the board. `key` is `qa_`-prefixed (stable id).
 */
export const quickActionSchema = z
    .object({
    key: z.string().regex(/^qa_[a-z0-9_]{1,30}$/, 'invalid quick-action key'),
    label: z.string().trim().min(1).max(24),
    stage: z.string().max(40).optional(),
    submitted: z.boolean().optional(),
})
    .refine((a) => (a.stage != null) !== (a.submitted != null), 'set exactly one of stage / submitted');
/** Build a `qa_` key from a label, e.g. "Not moving forward" → "qa_not_moving_forward". */
export const quickActionKey = (label) => {
    const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 30);
    return slug ? `qa_${slug}` : 'qa_action';
};
/** Default quick action: the common "Not moving forward" one-tap (sets the passed/declined lane). */
export const DEFAULT_QUICK_ACTIONS = [
    { key: 'qa_pass', label: 'Not moving forward', stage: 'passed' },
];
export const stageConfigSchema = z
    .object({
    /** Built-in stage keys hidden from the board (a populated lane is shown regardless). */
    hidden: z.array(z.string().max(40)).max(40).optional(),
    /** Stages whose entry should NOT notify the talent (on-enter action opt-out, per stage). */
    silent_stages: z.array(z.string().max(40)).max(40).optional(),
    /** Per-stage section visibility: stage key → which audition sections show when in that stage. */
    sections: z.record(z.string(), z.array(z.string().max(40)).max(10)).optional(),
    /** Custom kanban stages added beyond the built-in pipeline. */
    custom: z.array(customStageSchema).max(20).optional(),
    /** Customizable one-tap shortcut buttons shown on submitted cards (undefined ⇒ default, [] ⇒ none). */
    quick_actions: z.array(quickActionSchema).max(8).optional(),
    /** Which option the video Download chooser pre-selects: the full/original file, or the audition's
     *  required size (the per-audition download cap). Falls back to 'full' when no cap is set. */
    download_default: z.enum(['full', 'audition']).optional(),
    /** Let agents upload videos at full size (bypassing the on-device compression). Default OFF. */
    allow_full_uploads: z.boolean().optional(),
    /** When true, the talent portal shows NO activity feed at all (the agency hides all client activity). */
    hide_client_activity: z.boolean().optional(),
    /** Hide how early/late a tape went out vs its deadline on the board. For agencies that stamp the
     *  dashboard hours after the real submission — the offset reads "late" when the tape was on time. */
    hide_submit_timing: z.boolean().optional(),
    /** Let the talent see WHEN a tape was submitted in their portal feed. Off by default: the client
     *  sees only that it is submitted, never a date/time the agency may have stamped late. */
    show_client_submit_timing: z.boolean().optional(),
    /** Public website builder (@studiollama/sitebuilder) — off by default; gates the Website nav tab. */
    website_enabled: z.boolean().optional(),
})
    .partial();
/** The quick-action buttons for a business: its configured set, or the built-in default when unset. */
export function quickActionsFor(config) {
    return config?.quick_actions ?? DEFAULT_QUICK_ACTIONS;
}
/** Which download option to pre-select. Defaults to the audition's required size (the historical
 *  behavior); the chooser still falls back to 'full' when a given audition has no cap. */
export function downloadDefaultMode(config) {
    return config?.download_default ?? 'audition';
}
/** Whether agents may upload videos at full size (skip on-device compression). Default OFF. */
export function allowFullUploads(config) {
    return config?.allow_full_uploads ?? false;
}
/**
 * Should the board hide the submitted-vs-deadline offset? Default OFF (the timing shows), so agencies
 * that DO stamp submissions live keep the signal. On, a card that is submitted just reads "submitted".
 */
export function hideSubmitTiming(config) {
    return config?.hide_submit_timing ?? false;
}
/**
 * May the talent see submit TIMING (not just the submitted state) in their portal? Default OFF — a
 * dashboard stamped late would tell the client their tape went out late when it did not.
 */
export function showClientSubmitTiming(config) {
    return config?.show_client_submit_timing ?? false;
}
/** Quick lookup of custom stages by key (for validation + labels/colors). */
export function customStageMap(config) {
    return new Map((config?.custom ?? []).map((c) => [c.key, c]));
}
/**
 * The effective board lane for an audition: a VALID configured custom stage wins; otherwise its
 * built-in stage. (A custom_stage left dangling after its config is deleted falls back gracefully.)
 */
export function effectiveLane(stage, customStage, customKeys) {
    return customStage && customKeys.has(customStage) ? customStage : stage;
}
/** Insert each custom stage right after its `after` built-in lane; orphans (no/unknown after) go last. */
export function insertCustomLanes(builtin, custom) {
    if (!custom?.length)
        return builtin;
    const out = [];
    const placed = new Set();
    for (const s of builtin) {
        out.push(s);
        for (const c of custom) {
            if (c.after === s) {
                out.push(c.key);
                placed.add(c.key);
            }
        }
    }
    for (const c of custom)
        if (!placed.has(c.key))
            out.push(c.key);
    return out;
}
/**
 * Should this stage's column be hidden? Honors the agency's `hidden` list — but NEVER hides a lane
 * that currently holds cards, so customizing visibility can't strand auditions out of view.
 */
export function isStageHidden(config, stage, hasCards) {
    if (hasCards)
        return false;
    return Boolean(config?.hidden?.includes(stage));
}
/** On-enter action: notify the talent when an audition enters `stage`? (default yes; agency can silence per stage). */
export function stageNotifiesClient(config, stage) {
    return !config?.silent_stages?.includes(stage);
}
/**
 * Which optional sections show on an audition in `stage`. An agency can override per stage (e.g. only
 * "unlock" feedback at callback); with no override the defaults show, so behavior is unchanged by default.
 */
export function sectionsForStage(config, stage) {
    const override = config?.sections?.[stage];
    return new Set(override ?? DEFAULT_AUDITION_SECTIONS);
}
//# sourceMappingURL=stages.js.map